/**
 * Engine Trace — structured event log of every meaningful state change in
 * the playtest engine. Intended as a developer / admin-only debugging tool
 * (see EngineTracePanel + the bug icon in BoardTopBar). NOT a player log.
 *
 * Design principles:
 *   - Always-on. Every engine state-change call site emits an event. The
 *     panel decides what to show based on the user's filter. This keeps
 *     instrumentation simple — there are no "log levels" to reason about.
 *   - Self-locating. emit() captures the calling file:line:fn from a
 *     synthesized stack frame, so each entry points at the EXACT line that
 *     emitted it. Refactor-safe — moves with the code.
 *   - Bounded memory. Ring buffer (cap 2000 entries). No persistence
 *     (sandbox blocks localStorage / sessionStorage anyway).
 *   - Cheap. ~few microseconds per emit. Engine state changes are turn-
 *     scoped (a few hundred per round), so impact is invisible.
 *
 * The panel UI subscribes via subscribe() and re-renders on every push.
 * Non-admin users never mount the panel, so they don't pay any UI cost.
 *
 * Categories — see TraceCategory below. Pass 1 instruments:
 *   play, buff.queue, buff.fire, buff.expire, keyword.grant, keyword.consume,
 *   override.set, damage, focus.grant, focus.consume, round, hook.fire,
 *   stamina, error.
 *
 * To add new categories later: just call trace.emit({ category: "...",
 *   summary, details, label? }) — no central registration needed. Panel
 *   filter dropdown derives from the categories actually present.
 */

/** Loose union — extending it is allowed, just add to the constant array
 * below if you want it to appear in the panel filter dropdown by default.
 *
 * Pass 1 (engine state-changes): play, buff.*, keyword.*, override.set,
 *   damage, focus.*, stamina, round, hook.fire, error.
 *
 * Pass 2 (control flow): phase.transition, priority.shift, pass,
 *   chain.open/event/close, reaction.open/pass/close,
 *   counter.open/activate/resolve/fizzle/keep/discard,
 *   combat.start/slot.add/slot.resolve.
 *
 * Pass 3 (cards/zones/status): deck.draw, deck.shuffle, deck.fatigue,
 *   hand.discard, hand.remove, sig.play, sig.replace,
 *   equip.attach, equip.detach, armor.wear, armor.break,
 *   token.add, token.remove, utility.activate,
 *   status.stun.apply, status.stun.consume, status.exhaust,
 *   stance.set, stance.clear.
 *
 * Pass 4 (decisions/misc): prompt.open, prompt.resolve, coin.flip,
 *   heal, applyResults.dispatch. */
export type TraceCategory =
  | "play"
  | "buff.queue"
  | "buff.fire"
  | "buff.expire"
  | "keyword.grant"
  | "keyword.consume"
  | "override.set"
  | "damage"
  | "focus.grant"
  | "focus.consume"
  | "stamina"
  | "round"
  | "hook.fire"
  | "error"
  // ── Pass 2: control flow ─────────────────────────────────────────────
  | "phase.transition"
  | "priority.shift"
  | "pass"
  | "chain.open"
  | "chain.event"
  | "chain.close"
  | "reaction.open"
  | "reaction.pass"
  | "reaction.close"
  | "counter.open"
  | "counter.activate"
  | "counter.resolve"
  | "counter.fizzle"
  | "counter.keep"
  | "counter.discard"
  | "combat.start"
  | "combat.slot.add"
  | "combat.slot.resolve"
  // ── Pass 3: cards / zones / status ───────────────────────────────────
  | "deck.draw"
  | "deck.shuffle"
  | "deck.fatigue"
  | "hand.discard"
  | "hand.remove"
  | "sig.play"
  | "sig.replace"
  | "equip.attach"
  | "equip.detach"
  | "armor.wear"
  | "armor.break"
  | "token.add"
  | "token.remove"
  | "utility.activate"
  | "status.stun.apply"
  | "status.stun.consume"
  | "status.exhaust"
  | "stance.set"
  | "stance.clear"
  // ── Pass 4: decisions / misc ─────────────────────────────────────────
  | "prompt.open"
  | "prompt.resolve"
  | "coin.flip"
  | "heal"
  | "applyResults.dispatch"
  | (string & {});

/** Categories shown in the panel filter dropdown by default, in order.
 * Grouped: state-changes first, then control flow. */
export const KNOWN_CATEGORIES: TraceCategory[] = [
  // state-changes (Pass 1)
  "play",
  "buff.queue",
  "buff.fire",
  "buff.expire",
  "keyword.grant",
  "keyword.consume",
  "override.set",
  "damage",
  "focus.grant",
  "focus.consume",
  "stamina",
  "round",
  "hook.fire",
  "error",
  // control flow (Pass 2)
  "phase.transition",
  "priority.shift",
  "pass",
  "chain.open",
  "chain.event",
  "chain.close",
  "reaction.open",
  "reaction.pass",
  "reaction.close",
  "counter.open",
  "counter.activate",
  "counter.resolve",
  "counter.fizzle",
  "counter.keep",
  "counter.discard",
  "combat.start",
  "combat.slot.add",
  "combat.slot.resolve",
  // cards / zones / status (Pass 3)
  "deck.draw",
  "deck.shuffle",
  "deck.fatigue",
  "hand.discard",
  "hand.remove",
  "sig.play",
  "sig.replace",
  "equip.attach",
  "equip.detach",
  "armor.wear",
  "armor.break",
  "token.add",
  "token.remove",
  "utility.activate",
  "status.stun.apply",
  "status.stun.consume",
  "status.exhaust",
  "stance.set",
  "stance.clear",
  // decisions / misc (Pass 4)
  "prompt.open",
  "prompt.resolve",
  "coin.flip",
  "heal",
  "applyResults.dispatch",
];

export interface TraceSourceLocation {
  /** Path relative to repo root, e.g. "src/game/playtestEngine.ts". */
  file: string;
  /** 1-based line number of the trace.emit() call site. */
  line: number;
  /** 1-based column number, when available. */
  col?: number;
  /** Function name at that frame, when extractable. */
  fn?: string;
  /** Raw stack frame string, kept as fallback for hard-to-parse stacks. */
  raw?: string;
}

export interface TraceEntry {
  /** Monotonic per-session sequence number, 1-based. */
  seq: number;
  /** ms since epoch — Date.now(). */
  ts: number;
  /** Wall-clock ms since the FIRST emit this session — useful for diffs. */
  relMs: number;
  category: TraceCategory;
  /** One-line summary for the collapsed row. */
  summary: string;
  /** Optional short label that disambiguates multiple emits in one fn,
   * e.g. "pass1" / "pass2" inside firePendingBuffs. */
  label?: string;
  /** Free-form structured payload. Rendered as collapsible JSON. */
  details?: Record<string, unknown>;
  /** Game round at the time of emit, if known. */
  round?: number;
  /** Active player at time of emit (1 | 2), if known. */
  activePlayer?: 1 | 2;
  /** Source file:line:fn of the emit() call, captured from the stack. */
  source: TraceSourceLocation;
  /** First-class card linkage — lets the panel render a [card] chip that
   * jumps to /cards/<id>. Auto-resolved from cardName via the active
   * card map registered with setActiveCardMap(). */
  cardName?: string;
  cardId?: string;
}

/** Input shape for emit() — sequence/timestamp/source are filled in. */
export interface EmitInput {
  category: TraceCategory;
  summary: string;
  label?: string;
  details?: Record<string, unknown>;
  round?: number;
  activePlayer?: 1 | 2;
  /** Optional card linkage. Either id or name (or both); the resolver fills
   * in the missing side when possible. */
  cardName?: string;
  cardId?: string;
}

// ─── Card name ↔ id resolver ───────────────────────────────────────────
//
// Buffs only carry sourceCardName (no id), so we need a name→id lookup to
// build /cards/<id> jump links. PlaytestBoard registers the active card
// map (loaded from Supabase) on mount; the trace pipeline reads it on
// every emit. If unset (e.g. first emits before mount, or non-playtest
// pages), card linkage is skipped and the panel just shows cardName text.

type CardLike = { id: string; name: string };
let _activeCardMap: Map<string, CardLike> | null = null;
let _activeCardByName: Map<string, CardLike> | null = null;

/** Register the cards loaded for the current playtest session so trace
 * emits can resolve names↔ids automatically. Pass null to clear. */
export function setActiveCardMap(
  cards: ReadonlyMap<string, CardLike> | Iterable<CardLike> | null,
): void {
  if (!cards) {
    _activeCardMap = null;
    _activeCardByName = null;
    return;
  }
  const byId = new Map<string, CardLike>();
  const byName = new Map<string, CardLike>();
  // Normalize to a plain Map either way.
  const iter = (cards as ReadonlyMap<string, CardLike>).entries
    ? Array.from((cards as ReadonlyMap<string, CardLike>).values())
    : Array.from(cards as Iterable<CardLike>);
  for (const c of iter) {
    if (!c || !c.id || !c.name) continue;
    byId.set(c.id, c);
    byName.set(c.name.trim().toLowerCase(), c);
  }
  _activeCardMap = byId;
  _activeCardByName = byName;
}

function resolveCardLink(
  cardId: string | undefined,
  cardName: string | undefined,
): { cardId?: string; cardName?: string } {
  let id = cardId;
  let name = cardName;
  if (id && _activeCardMap) {
    const hit = _activeCardMap.get(id);
    if (hit && !name) name = hit.name;
  } else if (name && _activeCardByName) {
    const hit = _activeCardByName.get(name.trim().toLowerCase());
    if (hit) id = id ?? hit.id;
  }
  return { cardId: id, cardName: name };
}

const RING_CAP = 2000;

class TraceBuffer {
  private buf: TraceEntry[] = [];
  private seqCounter = 0;
  private firstTs: number | null = null;
  private subs = new Set<() => void>();
  /** When false, emit() short-circuits and does no work. Default true so we
   * capture from app start; admin gate only affects panel visibility. */
  private enabled = true;

  emit(input: EmitInput): TraceEntry | null {
    if (!this.enabled) return null;
    let source: TraceSourceLocation;
    try {
      source = captureCallerFrame();
    } catch {
      // Stack capture should never throw, but if it does we still emit
      // something useful rather than crashing the engine.
      source = { file: "<unknown>", line: 0 };
    }
    const ts = Date.now();
    if (this.firstTs === null) this.firstTs = ts;

    // Resolve card linkage. Honor explicit input.cardId/cardName first;
    // otherwise auto-promote common detail keys (cardId / cardName /
    // sourceCardName) so existing emits work without rewrites.
    const detailCardId =
      typeof input.details?.cardId === "string"
        ? (input.details.cardId as string)
        : undefined;
    const detailCardName =
      typeof input.details?.cardName === "string"
        ? (input.details.cardName as string)
        : typeof input.details?.card === "string"
          ? (input.details.card as string)
          : typeof input.details?.sourceCardName === "string"
            ? (input.details.sourceCardName as string)
            : undefined;
    const resolved = resolveCardLink(
      input.cardId ?? detailCardId,
      input.cardName ?? detailCardName,
    );

    const entry: TraceEntry = {
      seq: ++this.seqCounter,
      ts,
      relMs: ts - this.firstTs,
      category: input.category,
      summary: input.summary,
      label: input.label,
      details: input.details,
      round: input.round,
      activePlayer: input.activePlayer,
      source,
      cardName: resolved.cardName,
      cardId: resolved.cardId,
    };
    this.buf.push(entry);
    if (this.buf.length > RING_CAP) {
      // Drop oldest. Ring buffer kept as plain array for O(n) reads in panel
      // (n ≤ 2000 is trivial). We only pay the shift cost when over cap.
      this.buf.splice(0, this.buf.length - RING_CAP);
    }
    this.notify();
    return entry;
  }

  /** Convenience for catch-blocks. */
  emitError(message: string, err: unknown, extra?: Record<string, unknown>) {
    const detail = err instanceof Error
      ? { message: err.message, stack: err.stack }
      : { value: String(err) };
    return this.emit({
      category: "error",
      summary: message,
      details: { ...detail, ...extra },
    });
  }

  all(): readonly TraceEntry[] {
    return this.buf;
  }

  clear() {
    this.buf = [];
    this.seqCounter = 0;
    this.firstTs = null;
    this.notify();
  }

  setEnabled(v: boolean) {
    this.enabled = v;
  }

  isEnabled() {
    return this.enabled;
  }

  subscribe(fn: () => void): () => void {
    this.subs.add(fn);
    return () => this.subs.delete(fn);
  }

  /** Internal — used by the runtime sourcemap decoder to retroactively
   * patch source locations after the .map finishes loading. */
  _eachEntry(): TraceEntry[] {
    return this.buf;
  }
  /** Internal — trigger subscriber notification from outside the class. */
  _notifyExternal() {
    this.notify();
  }

  private notify() {
    for (const fn of this.subs) {
      try {
        fn();
      } catch {
        /* subscriber errors must not break the engine */
      }
    }
  }
}

/** Singleton instance. Prefer importing this rather than the class. */
export const trace = new TraceBuffer();

// Kick off sourcemap loading as soon as the module is imported in the
// browser. Idempotent and best-effort — we wait for window 'load' so the
// <script> tags are present in the DOM. In Node/SSR (no document) it's a
// no-op via the typeof check inside primeSourceMap().
if (typeof window !== "undefined") {
  if (document.readyState === "complete") {
    primeSourceMap();
  } else {
    window.addEventListener("load", () => primeSourceMap(), { once: true });
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Stack-frame capture
//
// Browsers format Error.stack differently:
//   Chrome / Edge / Node: "    at functionName (https://host/src/foo.ts:123:45)"
//                         "    at https://host/src/foo.ts:123:45"
//   Firefox:              "functionName@https://host/src/foo.ts:123:45"
//   Safari:               same as Firefox, sometimes missing function name
//
// Vite ships sourcemaps in dev (always) and in prod (we enable them in
// vite.config.ts). The browser resolves file URLs in stack traces against
// sourcemaps automatically, so frames already point at the original .ts
// source — we just need to extract them.
// ─────────────────────────────────────────────────────────────────────────

// ─── Runtime sourcemap decoding ────────────────────────────────────────
//
// Browsers only rewrite stack-trace file paths via sourcemaps when DevTools
// is OPEN. With DevTools closed (the normal case), Error.stack returns the
// bundled URL like "https://host/assets/index-XYZ.js:42:1234" — useless
// for jumping back to the original .ts file.
//
// To fix this, we lazily fetch the bundle's .map file once and decode the
// reported line:col back to the original {source, line, col} via
// source-map-js. Decoded results are cached. The fetch + initialization
// is async, so the very first events emitted before the map loads will
// fall back to the raw bundle path; subsequent emits reuse the consumer.

// Lazily-imported so source-map-js doesn't bloat first paint for non-admins.
// (The trace panel itself is admin-gated, but engineTrace is imported by
// playtestEngine which runs for everyone — so we keep this dynamic.)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SmcLike = { originalPositionFor: (p: { line: number; column: number }) => { source: string | null; line: number | null; column: number | null; name: string | null } };

let smcByBundle: Map<string, SmcLike> = new Map();
let smcInitPromiseByBundle: Map<string, Promise<SmcLike | null>> = new Map();
const decodeCache = new Map<string, TraceSourceLocation>();

async function loadSourceMapFor(bundleUrl: string): Promise<SmcLike | null> {
  // Strip query (?t=...) and ensure we hit foo.js.map.
  const clean = bundleUrl.replace(/\?[^\s)]*$/, "");
  if (!/\.js$/i.test(clean)) return null;
  const mapUrl = clean + ".map";
  try {
    const res = await fetch(mapUrl, { credentials: "omit" });
    if (!res.ok) return null;
    const json = await res.json();
    // Dynamic import — keeps the lib out of the critical bundle path.
    const mod = await import("source-map-js");
    const consumer = new mod.SourceMapConsumer(json) as unknown as SmcLike;
    return consumer;
  } catch {
    return null;
  }
}

function ensureSmc(bundleUrl: string): SmcLike | null {
  const clean = bundleUrl.replace(/\?[^\s)]*$/, "");
  const cached = smcByBundle.get(clean);
  if (cached) return cached;
  if (!smcInitPromiseByBundle.has(clean)) {
    smcInitPromiseByBundle.set(
      clean,
      loadSourceMapFor(clean).then((c) => {
        if (c) {
          smcByBundle.set(clean, c);
          // Re-decode any previously-captured frames that pointed at this
          // bundle so the panel updates retroactively.
          retroactivelyDecodeAll();
        }
        return c;
      }),
    );
  }
  return null; // not ready yet — caller falls back to raw frame
}

/** Walk the in-memory ring buffer and re-decode any bundled-file frames now
 * that a sourcemap has finished loading. Notifies subscribers so the panel
 * re-renders with fixed file:line. */
function retroactivelyDecodeAll(): void {
  let mutated = false;
  for (const e of trace._eachEntry()) {
    if (!e.source || !looksLikeBundledJs(e.source.file)) continue;
    const bundleUrl = extractBundleUrlFromRaw(e.source.raw ?? "") ?? e.source.file;
    const decoded = decodeFrame(
      bundleUrl,
      e.source.line,
      e.source.col ?? 0,
      e.source.fn,
      e.source.raw ?? "",
    );
    if (decoded) {
      // Mutate in place — the buffer is a private array.
      (e as { source: TraceSourceLocation }).source = decoded;
      mutated = true;
    }
  }
  if (mutated) trace._notifyExternal();
}

/** Eagerly start fetching the bundle's sourcemap as soon as engineTrace
 * loads. We discover the bundle URL by inspecting <script src> tags. In
 * dev (Vite) there's no single bundle — stack frames already point at the
 * .ts source, so this no-ops harmlessly. */
function primeSourceMap(): void {
  if (typeof document === "undefined") return;
  try {
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    for (const s of scripts) {
      const src = (s as HTMLScriptElement).src || "";
      if (looksLikeBundledJs(src)) {
        ensureSmc(src);
      }
    }
  } catch {
    /* ignore — best effort */
  }
}

/** Try to decode a bundled stack frame back to the original .ts source. */
function decodeFrame(
  bundleUrl: string,
  bundleLine: number,
  bundleCol: number,
  fnGuess: string | undefined,
  raw: string,
): TraceSourceLocation | null {
  const cleanUrl = bundleUrl.replace(/\?[^\s)]*$/, "");
  const cacheKey = `${cleanUrl}:${bundleLine}:${bundleCol}`;
  const memo = decodeCache.get(cacheKey);
  if (memo) return memo;

  const smc = ensureSmc(cleanUrl);
  if (!smc) return null;

  try {
    const orig = smc.originalPositionFor({ line: bundleLine, column: bundleCol });
    if (!orig.source || orig.line == null) return null;
    // Sources in the map are usually paths like "../../src/game/foo.ts" or
    // "src/game/foo.ts" — normalize to repo-relative.
    const file = trimToRepoRelative(orig.source.replace(/^\.\.\/+/g, ""));
    const out: TraceSourceLocation = {
      file,
      line: orig.line,
      col: orig.column ?? undefined,
      fn: orig.name ?? fnGuess,
      raw,
    };
    decodeCache.set(cacheKey, out);
    return out;
  } catch {
    return null;
  }
}

/** Patterns that match common bundler/runtime path prefixes we want to strip
 * so the displayed file path is repo-relative. Kept simple and additive. */
const PATH_TRIM_RE = /^.*?\/(src|client|server|shared)\//;

function trimToRepoRelative(absOrUrl: string): string {
  // Strip any ?query suffix Vite appends in dev (?t=12345&import).
  const cleaned = absOrUrl.replace(/\?[^\s)]*$/, "");
  // Try to chop everything before the first /src/ /client/ /server/ /shared/
  // segment so the displayed path matches what a developer sees in the
  // editor. Returns the matched segment + everything after.
  const m = cleaned.match(PATH_TRIM_RE);
  if (m && m.index !== undefined) {
    // m[0] is e.g. "http://host:5173/src/"; m[1] is e.g. "src". Keep "src/..."
    return cleaned.slice(m.index + m[0].length - (m[1].length + 1));
  }
  return cleaned;
}

/**
 * Synthesize an Error and walk its stack to find the first frame OUTSIDE
 * this file. That's the trace.emit() caller — i.e. the engine code that
 * we actually want to attribute the event to.
 */
function captureCallerFrame(): TraceSourceLocation {
  const err = new Error("trace");
  const stack = err.stack ?? "";
  const lines = stack.split("\n");

  // Skip frames that point back into THIS file. The first non-self frame
  // is the engine call site. We detect "self" by either the source filename
  // (dev / sourcemap-aware) OR by the fact that the decoded original file
  // is engineTrace.ts (prod minified).
  const SELF_MARKER = "engineTrace";

  for (const line of lines) {
    if (!line) continue;
    if (!/\d+:\d+/.test(line)) continue; // need :line:col to be a frame
    if (line.includes(SELF_MARKER)) continue;
    const parsed = parseStackLine(line);
    if (!parsed) continue;

    // If the parsed file is a bundled JS (e.g. /assets/index-XXX.js),
    // attempt to decode through the runtime sourcemap. The decoded frame
    // may itself be inside engineTrace.ts — in which case we keep walking.
    if (looksLikeBundledJs(parsed.file)) {
      const bundleUrl = extractBundleUrlFromRaw(parsed.raw ?? line) ?? parsed.file;
      const decoded = decodeFrame(
        bundleUrl,
        parsed.line,
        parsed.col ?? 0,
        parsed.fn,
        parsed.raw ?? line,
      );
      if (decoded) {
        if (decoded.file.includes(SELF_MARKER)) continue;
        return decoded;
      }
      // Sourcemap not ready yet — return the bundle frame so we still get
      // *something* useful (raw URL). Subsequent emits will decode once
      // the .map finishes loading.
      return parsed;
    }
    return parsed;
  }
  return { file: "<unknown>", line: 0, raw: stack };
}

/** True for bundle paths like "assets/index-XYZ.js" or full URLs to them. */
function looksLikeBundledJs(file: string): boolean {
  return /assets\/[^/?#]+\.js$/.test(file) || /\/assets\/[^/?#]+\.js$/.test(file);
}

/** Recover the original full bundle URL from a stack-trace raw line so we
 * can fetch its .map. trimToRepoRelative may have shortened it. */
function extractBundleUrlFromRaw(raw: string): string | null {
  const m = raw.match(/(https?:\/\/[^\s)]+\.js)(?::\d+:\d+)/);
  return m ? m[1] : null;
}

/** Parse one line of an Error.stack into {file, line, col, fn}. Tolerant
 * of Chrome/V8 and Firefox/Safari shapes. */
function parseStackLine(line: string): TraceSourceLocation | null {
  // Chrome:  "    at fnName (http://host/src/foo.ts?t=123:42:7)"
  //          "    at http://host/src/foo.ts:42:7"
  // Firefox: "fnName@http://host/src/foo.ts:42:7"
  const trimmed = line.trim();

  // Chrome with parens
  let m = trimmed.match(/^at\s+(.*?)\s+\((.*):(\d+):(\d+)\)$/);
  if (m) {
    return {
      fn: stripV8Suffix(m[1]),
      file: trimToRepoRelative(m[2]),
      line: parseInt(m[3], 10),
      col: parseInt(m[4], 10),
      raw: trimmed,
    };
  }

  // Chrome without parens (anonymous): "at http://host/src/foo.ts:42:7"
  m = trimmed.match(/^at\s+(.*):(\d+):(\d+)$/);
  if (m) {
    return {
      file: trimToRepoRelative(m[1]),
      line: parseInt(m[2], 10),
      col: parseInt(m[3], 10),
      raw: trimmed,
    };
  }

  // Firefox / Safari: "fnName@http://host/src/foo.ts:42:7"
  m = trimmed.match(/^(.*?)@(.*):(\d+):(\d+)$/);
  if (m) {
    return {
      fn: m[1] || undefined,
      file: trimToRepoRelative(m[2]),
      line: parseInt(m[3], 10),
      col: parseInt(m[4], 10),
      raw: trimmed,
    };
  }

  return null;
}

/** V8 sometimes appends " [as method]" or wraps "Object.fnName" / "async fn".
 * Strip those for cleaner display. */
function stripV8Suffix(fn: string): string | undefined {
  if (!fn) return undefined;
  return fn
    .replace(/^async\s+/, "")
    .replace(/^new\s+/, "")
    .replace(/\s+\[as\s+[^\]]+\]$/, "")
    .replace(/^Object\./, "")
    .trim() || undefined;
}

/** Format a source location as "src/game/foo.ts:123" — used for the
 * one-line display and the [copy] button payload. */
export function formatSource(s: TraceSourceLocation): string {
  if (!s || s.file === "<unknown>") return "<unknown>";
  if (looksLikeBundledJs(s.file)) return `(decoding…) ${s.line}:${s.col ?? 0}`;
  return `${s.file}:${s.line}`;
}

/** Build a vscode://file/... URL the panel's [open] button can navigate to.
 * Without an absolute repoRoot, VS Code can't resolve a relative path and
 * shows "file doesn't exist". Pass repoRoot (e.g. "/Users/noah/dev/guild-masters")
 * to build a working URL. The trace panel persists this in component state
 * so the user only sets it once per session.
 */
export function vscodeOpenUrl(
  s: TraceSourceLocation,
  repoRoot?: string | null,
): string | null {
  if (!s || s.file === "<unknown>") return null;
  if (looksLikeBundledJs(s.file)) return null;
  if (repoRoot && repoRoot.trim().length > 0) {
    // Trim trailing slashes to avoid "//src/..." double-separator.
    const root = repoRoot.replace(/\/+$/, "");
    return `vscode://file/${root}/${s.file}:${s.line}`;
  }
  // Relative fallback — only useful if the user has a custom protocol
  // handler. Most users should configure repoRoot via the panel's settings.
  return `vscode://file/${s.file}:${s.line}`;
}

// ─── GitHub link builder ────────────────────────────────────────────────
//
// Vite injects __GIT_COMMIT_SHA__ at build time (see vite.config.ts) so we
// can link to the exact commit that produced the trace. If the SHA is
// missing (older builds, dev hot-reload edge cases) we fall back to "main".

declare const __GIT_COMMIT_SHA__: string | undefined;

const GITHUB_REPO = "GuildMasters2026/guild-masters";

function resolveCommitSha(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sha = (globalThis as any).__GIT_COMMIT_SHA__ ??
      (typeof __GIT_COMMIT_SHA__ !== "undefined" ? __GIT_COMMIT_SHA__ : undefined);
    if (typeof sha === "string" && sha.length > 0) return sha;
  } catch {
    /* ignore */
  }
  return "main";
}

/** Build a github.com/.../blob/<sha>/<file>#L<line> URL. Returns null if
 * the source location is unknown or still pointing at a bundled JS path
 * (sourcemap not yet decoded). The panel uses null to disable the menu
 * action and show "decoding…". */
export function githubOpenUrl(s: TraceSourceLocation): string | null {
  if (!s || s.file === "<unknown>") return null;
  if (looksLikeBundledJs(s.file)) return null;
  const sha = resolveCommitSha();
  return `https://github.com/${GITHUB_REPO}/blob/${sha}/${s.file}#L${s.line}`;
}

/** True if the given source still points at a bundle (sourcemap pending). */
export function isPendingDecode(s: TraceSourceLocation): boolean {
  return !!s && looksLikeBundledJs(s.file);
}

/** Expose the captured SHA so the panel can show it in the footer. */
export function getBuildCommitSha(): string {
  return resolveCommitSha();
}

/** Build a GitHub code-search URL that finds occurrences of a card's name
 * inside the engine source (playtestEngine.ts) — and the broader src/game/
 * directory as a fallback. This is how you locate hardcoded card-specific
 * logic (e.g. Channel Focus → +Piercing on next attack at
 * playtestEngine.ts:2698). The search is scoped to the repo and a single
 * file path so results are highly relevant.
 *
 * GitHub's code search supports the `repo:` and `path:` qualifiers and
 * exact-match via double quotes. We url-encode the whole thing.
 */
export function githubSearchCardCodeUrl(cardName: string): string {
  const quoted = `"${cardName}"`;
  // Search inside src/game/ where engine + name-matching logic lives.
  const query = `repo:${GITHUB_REPO} path:src/game/ ${quoted}`;
  return `https://github.com/search?q=${encodeURIComponent(query)}&type=code`;
}
